import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Sparkle, Car, Wrench, Package, Calculator, ChatCircle, CalendarBlank,
  Storefront, Motorcycle, Handshake, Tag, Megaphone, Briefcase, IdentificationCard, Star,
  Plus, Trash, ArrowUp, ArrowDown,
  ArrowClockwise, FloppyDisk, CircleNotch, Robot,
} from "@phosphor-icons/react";

const ICON_MAP = {
  Sparkle, Car, Wrench, Package, Calculator, ChatCircle, CalendarBlank,
  Storefront, Motorcycle, Handshake,
  Tag, Megaphone, Briefcase, IdCard: IdentificationCard, Star,
};
const ICON_NAMES = Object.keys(ICON_MAP);

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30) || "chip";
}

export default function WidgetChipsEditor({ lang }) {
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/settings/widget-chips")
      .then((r) => { if (alive) { setChips(r.data.chips || []); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  function updateChip(idx, patch) {
    setChips((cs) => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    setDirty(true);
  }
  function move(idx, dir) {
    setChips((cs) => {
      const target = idx + dir;
      if (target < 0 || target >= cs.length) return cs;
      const copy = [...cs];
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy;
    });
    setDirty(true);
  }
  function remove(idx) {
    setChips((cs) => cs.filter((_, i) => i !== idx));
    setDirty(true);
  }
  function addChip() {
    if (chips.length >= 10) {
      toast.error(lang === "es" ? "Máximo 10 chips" : "Max 10 chips");
      return;
    }
    setChips((cs) => [
      ...cs,
      {
        id: `chip-${Date.now().toString(36).slice(-4)}`,
        icon: "Sparkle",
        title: lang === "es" ? "Nuevo chip" : "New chip",
        subtitle: "",
        prompt: "",
        enabled: true,
      },
    ]);
    setDirty(true);
  }

  async function save() {
    // Ensure ids are unique and slugified
    const seen = new Set();
    const cleaned = chips.map((c) => {
      let id = slugify(c.id || c.title);
      let base = id, n = 2;
      while (seen.has(id)) { id = `${base}-${n++}`; }
      seen.add(id);
      return { ...c, id };
    });
    if (cleaned.some((c) => !c.title.trim())) {
      toast.error(lang === "es" ? "Cada chip requiere título" : "Each chip needs a title");
      return;
    }
    if (cleaned.some((c) => !c.prompt.trim())) {
      toast.error(lang === "es" ? "Cada chip requiere un prompt" : "Each chip needs a prompt");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/widget-chips", { chips: cleaned });
      setChips(data.chips);
      setDirty(false);
      toast.success(lang === "es" ? "Chips guardados" : "Chips saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function resetDefaults() {
    if (!window.confirm(lang === "es"
      ? "¿Restaurar los chips por defecto? Perderás los cambios actuales."
      : "Restore default chips? Current changes will be lost.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/settings/widget-chips/reset");
      setChips(data.chips);
      setDirty(false);
      toast.success(lang === "es" ? "Chips restaurados" : "Chips restored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CircleNotch size={14} className="animate-spin" />
        {lang === "es" ? "Cargando chips…" : "Loading chips…"}
      </div>
    );
  }

  return (
    <div data-testid="widget-chips-editor" className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
      {/* Left column — editor */}
      <div>
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="text-sm font-medium">
            {lang === "es" ? "Chips del widget" : "Widget chips"}
          </div>
          <div className="text-xs text-muted-foreground">
            {lang === "es"
              ? "Opciones guiadas que aparecen al abrir el chat web. Reordena, edita o desactiva."
              : "Guided options shown when the web chat opens. Reorder, edit or disable."}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={resetDefaults} disabled={saving} data-testid="chips-reset">
            <ArrowClockwise size={12} className="mr-1" />
            {lang === "es" ? "Restaurar" : "Reset"}
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !dirty} data-testid="chips-save">
            {saving ? <CircleNotch size={12} className="animate-spin mr-1" /> : <FloppyDisk size={12} className="mr-1" />}
            {lang === "es" ? "Guardar" : "Save"}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {chips.map((chip, idx) => {
          const Icon = ICON_MAP[chip.icon] || Sparkle;
          return (
            <div
              key={idx}
              className={`border rounded-md p-3 bg-background/40 ${chip.enabled === false ? "opacity-60" : ""}`}
              data-testid={`chip-row-${chip.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1">
                  <button
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0 || saving}
                    aria-label="Move up"
                    data-testid={`chip-up-${chip.id}`}
                  ><ArrowUp size={12} /></button>
                  <button
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                    onClick={() => move(idx, 1)}
                    disabled={idx === chips.length - 1 || saving}
                    aria-label="Move down"
                    data-testid={`chip-down-${chip.id}`}
                  ><ArrowDown size={12} /></button>
                </div>

                <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Icon size={16} weight="regular" />
                </div>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-3">
                    <label className="text-[10px] uppercase text-muted-foreground">
                      {lang === "es" ? "Ícono" : "Icon"}
                    </label>
                    <select
                      value={chip.icon}
                      onChange={(e) => updateChip(idx, { icon: e.target.value })}
                      className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                      data-testid={`chip-icon-${chip.id}`}
                    >
                      {ICON_NAMES.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-9">
                    <label className="text-[10px] uppercase text-muted-foreground">
                      {lang === "es" ? "Título" : "Title"}
                    </label>
                    <Input
                      value={chip.title}
                      onChange={(e) => updateChip(idx, { title: e.target.value })}
                      placeholder={lang === "es" ? "Ej. Cita de Taller" : "e.g. Book a service"}
                      className="h-8"
                      data-testid={`chip-title-${chip.id}`}
                    />
                  </div>
                  <div className="md:col-span-12">
                    <label className="text-[10px] uppercase text-muted-foreground">
                      {lang === "es" ? "Subtítulo" : "Subtitle"}
                    </label>
                    <Input
                      value={chip.subtitle}
                      onChange={(e) => updateChip(idx, { subtitle: e.target.value })}
                      placeholder={lang === "es" ? "Descripción corta" : "Short description"}
                      className="h-8"
                      data-testid={`chip-subtitle-${chip.id}`}
                    />
                  </div>
                  <div className="md:col-span-12">
                    <label className="text-[10px] uppercase text-muted-foreground">
                      {lang === "es" ? "Prompt (lo que le llegará al Concierge)" : "Prompt (sent to the Concierge)"}
                    </label>
                    <Textarea
                      value={chip.prompt}
                      onChange={(e) => updateChip(idx, { prompt: e.target.value })}
                      placeholder={lang === "es" ? "Mensaje inicial que se le envía al AI" : "Initial message sent to the AI"}
                      rows={2}
                      data-testid={`chip-prompt-${chip.id}`}
                    />
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase text-muted-foreground">
                      {lang === "es" ? "Activo" : "Enabled"}
                    </span>
                    <Switch
                      checked={chip.enabled !== false}
                      onCheckedChange={(v) => updateChip(idx, { enabled: v })}
                      data-testid={`chip-enabled-${chip.id}`}
                    />
                  </div>
                  <button
                    className="text-destructive hover:bg-destructive/10 p-1.5 rounded-md"
                    onClick={() => remove(idx)}
                    aria-label="Delete"
                    data-testid={`chip-delete-${chip.id}`}
                  ><Trash size={14} /></button>
                </div>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Badge variant="outline" className="text-[9px] font-mono">
                  id: {chip.id}
                </Badge>
              </div>
            </div>
          );
        })}

        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={addChip}
          disabled={saving || chips.length >= 10}
          data-testid="chips-add"
        >
          <Plus size={14} className="mr-1" />
          {lang === "es" ? "Agregar chip" : "Add chip"}
          <span className="ml-2 text-[10px] text-muted-foreground">
            {chips.length}/10
          </span>
        </Button>
      </div>
      </div>

      {/* Right column — Live widget preview */}
      <WidgetPreview chips={chips} lang={lang} dirty={dirty} />
    </div>
  );
}

/**
 * Live preview mimicking the ChatWidget's guided panel.
 * Read-only: chips are shown as they would appear in the widget,
 * respecting order, icon, title, subtitle and `enabled` flag.
 */
function WidgetPreview({ chips, lang, dirty }) {
  const visible = chips.filter((c) => c.enabled !== false);
  return (
    <aside
      data-testid="widget-preview"
      className="lg:sticky lg:top-24 self-start"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          {lang === "es" ? "Vista previa en vivo" : "Live preview"}
        </div>
        {dirty && (
          <span
            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 border border-amber-500/30"
            data-testid="preview-dirty"
          >
            {lang === "es" ? "Sin guardar" : "Unsaved"}
          </span>
        )}
      </div>

      {/* Phone-like frame matching the widget aesthetic */}
      <div className="rounded-2xl border border-border bg-background shadow-lg overflow-hidden">
        {/* Header bar */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-card">
          <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Robot size={16} weight="regular" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-heading font-semibold truncate">SDM Concierge</div>
            <div className="text-[9px] text-emerald-600 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
              {lang === "es" ? "En línea · Powered by AI" : "Online · Powered by AI"}
            </div>
          </div>
        </div>

        {/* Body — greeting bubble + guided panel */}
        <div className="p-3 bg-muted/20 space-y-3 min-h-[280px]">
          <div className="flex gap-2 items-start">
            <div className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
              <Robot size={11} />
            </div>
            <div className="rounded-lg rounded-tl-sm bg-card border border-border px-3 py-2 text-[11px] leading-relaxed max-w-[80%]">
              {lang === "es"
                ? "Hola 👋, soy tu Asistente Virtual Santo Domingo Motors. Estoy aquí para asistirte con todo lo que necesites."
                : "Hi! I'm your SDM Concierge, how can I help today?"}
            </div>
          </div>

          <div className="pt-1">
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
              <Sparkle size={9} weight="fill" />
              {lang === "es" ? "¿En qué te ayudamos hoy?" : "How can we help today?"}
            </div>
            {visible.length === 0 ? (
              <div className="text-[10px] text-muted-foreground italic border border-dashed border-border rounded-md p-3 text-center">
                {lang === "es"
                  ? "No hay chips activos. El widget solo mostrará el input de texto."
                  : "No active chips. The widget will only show the text input."}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-1.5">
                {visible.map((chip) => {
                  const Icon = ICON_MAP[chip.icon] || Sparkle;
                  return (
                    <div
                      key={chip.id}
                      className="flex items-start gap-2 text-left p-2 rounded-md border border-border bg-card"
                      data-testid={`preview-chip-${chip.id}`}
                    >
                      <div className="w-7 h-7 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon size={13} weight="regular" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-semibold text-[11px] leading-tight truncate">
                          {chip.title || <span className="text-muted-foreground italic">
                            {lang === "es" ? "Sin título" : "Untitled"}
                          </span>}
                        </div>
                        {chip.subtitle && (
                          <div className="text-[9.5px] text-muted-foreground line-clamp-2 mt-0.5">
                            {chip.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Input bar (visual only) */}
        <div className="flex items-center gap-2 p-2 border-t border-border bg-card">
          <div className="flex-1 h-7 rounded-full border border-border bg-background px-3 flex items-center text-[10px] text-muted-foreground">
            {lang === "es" ? "Escribe tu mensaje…" : "Type your message…"}
          </div>
          <div className="w-7 h-7 rounded-full bg-primary/80 flex items-center justify-center">
            <Sparkle size={11} className="text-primary-foreground" />
          </div>
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground mt-2 flex items-center justify-between">
        <span>{visible.length} / {chips.length} {lang === "es" ? "chips activos" : "chips active"}</span>
        <a
          href="/widget"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-primary"
          data-testid="preview-open-widget"
        >
          {lang === "es" ? "Abrir widget real →" : "Open real widget →"}
        </a>
      </div>
    </aside>
  );
}
